import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import api from '../../services/api';
import FilterAutocomplete from '../../components/Common/FilterAutocomplete';

/**
 * Transferências de bilhetes (pg_evento_bilhete_transferencias).
 * O bilhete é o mesmo: a transferência muda o titular e o código — o QR antigo
 * deixa de entrar. Cada linha diz de quem para quem e o código antigo → novo.
 */
interface Transferencia {
  id: number;
  created_at: string;
  evento_id: number;
  evento_titulo?: string | null;
  tipo_nome?: string | null;
  codigo_anterior: string;
  codigo_novo: string;
  codigo_atual?: string | null;
  de_nome?: string | null;
  de_email?: string | null;
  para_nome?: string | null;
  para_email?: string | null;
  pedido_codigo?: string | null;
}

const fmtData = (s: string) =>
  new Date(s).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const Transferencias: React.FC = () => {
  const [rows, setRows] = useState<Transferencia[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [pesquisa, setPesquisa] = useState('');
  const [q, setQ] = useState('');
  const [evento, setEvento] = useState('all');

  useEffect(() => {
    const t = setTimeout(() => setQ(pesquisa.trim()), 300);
    return () => clearTimeout(t);
  }, [pesquisa]);

  useEffect(() => {
    let vivo = true;
    setLoading(true);
    setErro(null);
    api.get('/admin/events/transfers', { params: { limit: 500, ...(q ? { q } : {}) } })
      .then((r) => { if (vivo) setRows(r.data?.data ?? []); })
      .catch((e) => { if (vivo) setErro(e?.response?.data?.error || 'Não foi possível carregar as transferências.'); })
      .finally(() => { if (vivo) setLoading(false); });
    return () => { vivo = false; };
  }, [q]);

  // Só os eventos que têm transferências — a lista de todos os eventos seria enorme.
  const opcoesEvento = useMemo(() => {
    const m = new Map<string, string>();
    rows.forEach((r) => m.set(String(r.evento_id), r.evento_titulo || `Evento ${r.evento_id}`));
    return [...m.entries()].map(([value, label]) => ({ value, label }));
  }, [rows]);

  const visiveis = evento === 'all' ? rows : rows.filter((r) => String(r.evento_id) === evento);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>Transferências de bilhetes</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        O bilhete transferido é o mesmo: muda o titular e o código. O código antigo (riscado) já não entra.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Nome, email ou código do bilhete"
          value={pesquisa}
          onChange={(e) => setPesquisa(e.target.value)}
          sx={{ minWidth: 300, flex: 1 }}
          slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
        />
        <Box sx={{ minWidth: 280 }}>
          <FilterAutocomplete label="Evento" value={evento} onChange={setEvento} options={opcoesEvento} placeholderTodos="Todos" />
        </Box>
      </Box>

      {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

      <Paper variant="outlined">
        {loading ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress size={28} /></Box>
        ) : visiveis.length === 0 ? (
          <Typography sx={{ p: 4, textAlign: 'center' }} color="text.secondary">
            {q || evento !== 'all' ? 'Nenhuma transferência encontrada.' : 'Ainda não houve transferências de bilhetes.'}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Evento</TableCell>
                  <TableCell>Bilhete</TableCell>
                  <TableCell>De</TableCell>
                  <TableCell>Para</TableCell>
                  <TableCell>Código antigo → novo</TableCell>
                  <TableCell>Encomenda</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visiveis.map((t) => (
                  <TableRow key={t.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtData(t.created_at)}</TableCell>
                    <TableCell>{t.evento_titulo || `Evento ${t.evento_id}`}</TableCell>
                    <TableCell>{t.tipo_nome || '—'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{t.de_nome || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">{t.de_email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{t.para_nome || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">{t.para_email}</Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: 12 }}>
                      <Box component="span" sx={{ color: 'error.main', textDecoration: 'line-through' }}>{t.codigo_anterior}</Box>
                      {' → '}
                      <Box component="span" sx={{ color: 'primary.main' }}>{t.codigo_novo}</Box>
                      {t.codigo_atual && t.codigo_atual !== t.codigo_novo && (
                        <Typography variant="caption" color="text.secondary" display="block">voltou a ser transferido</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{t.pedido_codigo || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      {!loading && visiveis.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, textAlign: 'right' }}>
          {visiveis.length} transferência{visiveis.length !== 1 ? 's' : ''}
        </Typography>
      )}
    </Box>
  );
};

export default Transferencias;
